/**
 * ORION — Visual Domain
 *
 * File: ObjectInspector.tsx
 *
 * Responsibility:
 * - Micro-view of a single Cognitive Object.
 * - Container that composes Governance (Voting) and Observability (Timeline) UI.
 * - Displays metadata and allows interactions (Transitions).
 *
 * What this file does NOT do:
 * - Does not implement the voting logic itself (delegates to VotingPanel).
 * - Does not manage the SSE stream directly (delegates to ReplayTimeline hooks).
 *
 * Depends on:
 * - VotingPanel (Governance)
 * - ReplayTimeline (Observability)
 */
import React from 'react';
import type { CognitiveObject } from '../cognition/cognitionService';
import { VotingPanel } from '../governance/ui/VotingPanel';
import { ReplayTimeline } from '../observability/ui/ReplayTimeline';

interface ObjectInspectorProps {
    object: CognitiveObject;
}

export const ObjectInspector: React.FC<ObjectInspectorProps> = ({ object }) => {
    // Guard clause: Ensure object exists
    if (!object) {
        return (
            <div className="flex items-center justify-center h-full bg-slate-950 text-slate-500">
                <span className="font-mono text-sm">Waiting for object data...</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-slate-950 text-white overflow-hidden">
            {/* 1. Header & Metadata */}
            <div className="p-6 border-b border-gray-800 bg-slate-900/50">
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-2xl font-bold font-mono text-cyan-400">
                            {object.metadata?.title || 'Untitled Object'}
                        </h1>
                        <div className="flex items-center space-x-3 mt-2 text-sm text-gray-400 font-mono">
                            <span className="bg-slate-800 px-2 py-0.5 rounded border border-slate-700">
                                {object.object_type}
                            </span>
                            <span>ID: {object.id}</span>
                            <span>v{object.version}</span>
                        </div>
                    </div>
                    <div className="text-right">
                        <span className={`
                            inline-block px-3 py-1 rounded-full text-sm font-bold tracking-wide
                            ${getStatusColor(object.current_state)}
                        `}>
                            {object.current_state}
                        </span>
                    </div>
                </div>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8">

                {/* 2. Governance Section (Voting) */}
                <section>
                    <div className="flex items-center mb-4 space-x-2">
                        <span className="text-xl">🗳️</span>
                        <h2 className="text-lg font-semibold text-gray-200">Governance & Voting</h2>
                    </div>
                    <div className="bg-slate-900 rounded-lg p-1">
                        <VotingPanel
                            objectId={object.id}
                            objectState={object.current_state}
                            onUpdate={() => { }} // Simple refresh for now
                            isActiveTab={true}
                        />
                    </div>
                </section>

                {/* 3. Observability Placeholder */}
                <section className="opacity-75">
                    <div className="flex items-center mb-4 space-x-2">
                        <span className="text-xl">📡</span>
                        <h2 className="text-lg font-semibold text-gray-200">Observability & Timeline</h2>
                    </div>

                    <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
                        <ReplayTimeline objectId={object.id} />
                    </div>
                </section>

                {/* Content Preview (Extra, minimal) */}
                <section className="opacity-90">
                    <div className="flex items-center mb-4 space-x-2">
                        <span className="text-xl">📄</span>
                        <h2 className="text-lg font-semibold text-gray-200">Content Preview</h2>
                    </div>
                    <div className="bg-slate-900 rounded p-4 font-mono text-xs text-gray-400 whitespace-pre-wrap max-h-60 overflow-y-auto border border-slate-800">
                        {JSON.stringify(object.content || {}, null, 2)}
                    </div>
                </section>

            </div>
        </div>
    );
};

function getStatusColor(status: string): string {
    switch (status) {
        case 'DECIDED': return 'bg-green-900/50 text-green-400 border border-green-800';
        case 'FINALIST': return 'bg-emerald-900/50 text-emerald-400 border border-emerald-800';
        case 'STRESS_TEST': return 'bg-orange-900/50 text-orange-400 border border-orange-800';
        case 'POLLINATION': return 'bg-blue-900/50 text-blue-400 border border-blue-800';
        case 'CREATED': return 'bg-gray-800 text-gray-300 border border-gray-700';
        case 'PAUSED': return 'bg-yellow-900/50 text-yellow-500 border border-yellow-800';
        case 'AWAITING_HUMAN': return 'bg-red-900/50 text-red-400 border border-red-800 animate-pulse';
        default: return 'bg-slate-800 text-slate-300 border border-slate-700';
    }
}
